import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import OriParticles from "@/components/ori/OriParticles";
import googleIcon from "@/assets/integrations/google.svg";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Only react to real sign-in / sign-out — ignore TOKEN_REFRESHED / USER_UPDATED
      // so we never re-route the user mid-session (defensive).
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT") return;
      if (event === "SIGNED_IN" && session) {
        checkOnboarding(session.user.id);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) checkOnboarding(session.user.id);
    });
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  const checkOnboarding = async (userId: string) => {
    const { data } = await supabase
      .from("clients")
      .select("id, positioning_completed_at, website_url")
      .eq("user_id", userId);

    const positioned = (data ?? []).some((c) => !!c.positioning_completed_at);
    if (positioned) {
      navigate("/dashboard");
    } else {
      sessionStorage.removeItem("orbit_intro_seen");
      navigate("/onboarding-intro");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    } else {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) {
        const code = (error as any).code;
        if (code === "user_already_exists" || /already registered|already exists/i.test(error.message)) {
          setError("This email is already registered. Try logging in instead.");
        } else {
          setError(error.message);
        }
      } else if (data.user && data.user.identities && data.user.identities.length === 0) {
        // Supabase returns a user with empty identities when the email already exists
        setError("This email is already registered. Try logging in instead.");
      } else if (data.session) {
        // Auto-confirm enabled — user is logged in, redirect handled by onAuthStateChange
      } else {
        setMessage("Check your email to confirm your account, then log in.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <OriParticles density="low" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-sm mx-4"
      >
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-6">
          <h1 className="text-xl font-mono text-white/90 mb-1 text-center">
            {isLogin ? "Welcome back" : "Create account"}
          </h1>
          <p className="text-xs font-mono text-white/30 mb-6 text-center">
            {isLogin ? "Log in to your dashboard" : "Start your SEO journey"}
          </p>

          <button
            type="button"
            onClick={async () => {
              setError(null);
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: `${window.location.origin}/dashboard`,
              });
              if (result.error) setError((result.error as any).message ?? "Google sign-in failed");
            }}
            className="w-full flex items-center justify-center gap-2 bg-white text-black font-mono font-semibold text-sm py-3 rounded-lg hover:bg-white/90 transition-all mb-4"
          >
            <img src={googleIcon} alt="" className="w-4 h-4" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#1a1a1a]" />
            <span className="text-xs font-mono text-white/30">or</span>
            <div className="flex-1 h-px bg-[#1a1a1a]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-black border border-[#1a1a1a] rounded-lg px-4 py-3 text-sm font-mono text-white/80 placeholder:text-white/20 focus:outline-none focus:border-[#00ff88]/30"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-black border border-[#1a1a1a] rounded-lg px-4 py-3 text-sm font-mono text-white/80 placeholder:text-white/20 focus:outline-none focus:border-[#00ff88]/30"
            />

            {error && (
              <p className="text-xs font-mono text-red-400/80">{error}</p>
            )}
            {message && (
              <p className="text-xs font-mono text-[#00ff88]/80">{message}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00ff88] text-black font-mono font-semibold text-sm py-3 rounded-lg hover:bg-[#00ff88]/90 transition-all disabled:opacity-50"
            >
              {loading ? "..." : isLogin ? "Log in" : "Sign up"}
            </button>
          </form>

          <button
            onClick={() => { setIsLogin(!isLogin); setError(null); setMessage(null); }}
            className="w-full mt-4 text-xs font-mono text-white/30 hover:text-white/50 transition-colors"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Log in"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
